<template>
  <div class="q-page">
    <div class="fill-height q-px-xl q-pt-xl bg-accounting-plan-header">
      <!-- ---------------- Accounting Plans ---------------- -->
      <div class="row q-mt-none items-center justify-between q-mb-none">
        <div class="col-shrink text-left text-bold">
          <q-select
            v-model="accounting_plans_selected"
            dense
            :options="accounting_plans"
            emit-value
            option-label="name"
            option-value="link"
            filled
            hide-bottom-space
            :disable="accounting_plans.length === 0"
            bg-color="transparent"
          >
            <template v-slot:selected>
              <span class="text-h5">{{ accounting_plan?.name }}</span>
            </template>
          </q-select>
        </div>
        <div class="col-shrink row items-center">
          <!-- Edit  -->
          <q-btn
            color="primary"
            round
            icon="more_vert"
            class="q-mr-md"
            flat
            @click.stop
          >
            <q-menu>
              <q-list style="min-width: 220px">
                <!-- edit -->
                <q-item
                  v-close-popup
                  clickable
                  @click="accounting_plans_edit(accounting_plan)"
                >
                  <q-item-section avatar>
                    <q-icon
                      name="las la-pen"
                      class="q-pr-none"
                    />
                  </q-item-section>
                  <q-item-section>{{ i18n.edit }}</q-item-section>
                </q-item>

                <!-- Duplicate -->
                <q-item
                  v-if="accounting_plan"
                  v-close-popup
                  clickable
                  @click="accounting_plans_duplicate_confirm(accounting_plan)"
                >
                  <q-item-section avatar>
                    <q-icon
                      name="las la-copy"
                      class="q-pr-none"
                    />
                  </q-item-section>
                  <q-item-section>{{ i18n.accounting_plans_duplicate }}</q-item-section>
                </q-item>

                <!-- Delete -->
                <q-item
                  v-close-popup
                  clickable
                  @click="accounting_plans_delete_confirm(accounting_plan)"
                >
                  <q-item-section avatar>
                    <q-icon
                      name="las la-trash-alt"
                      class="q-pr-none"
                    />
                  </q-item-section>
                  <q-item-section>{{ i18n.delete }}</q-item-section>
                </q-item>
              </q-list>
            </q-menu>
          </q-btn>

          <!-- Create with Menu -->
          <q-btn
            unelevated
            color="primary"
            outline
            icon="las la-plus"
            :label="i18n.accounting_plans_create"
            no-caps
            @click="accounting_plans_edit({})"
          />
        </div>
      </div>

      <div class="row q-mt-none items-center justify-between q-mb-md">
        <p
          class="q-mb-none text-caption"
          style="padding-left: 12px"
        >
          {{ accounting_plan?.description }}
        </p>
      </div>

      <!-- Display selected accounting plan -->
      <template v-if="accounting_plan">
        <!-- Tabs selector -->
        <div class="row justify-start q-col-gutter-md full_width q-mb-none q-mt-md">
          <div class="col-auto text-left">
            <q-tabs
              v-model="page_current"
              inline-label
              active-color="primary"
              no-caps
              dense
            >
              <template v-if="$q.screen.xs">
                <!-- small screens (2 tabs + menu dropdown) -->
                <template
                  v-for="(p, index) in pages.slice(0, 2)"
                  :key="index"
                >
                  <q-tab
                    :name="p.name"
                    :label="p.label"
                    @click="page_select(p.link)"
                  />
                </template>

                <q-btn-dropdown
                  auto-close
                  stretch
                  flat
                  :label="dropdownLabel"
                  no-caps
                  :class="dropdownClass"
                >
                  <q-list>
                    <template
                      v-for="(p, index) in pages.slice(2)"
                      :key="index"
                    >
                      <q-item
                        clickable
                        @click="page_select(p.link)"
                      >
                        <q-item-section>{{ p.label }}</q-item-section>
                      </q-item>
                    </template>
                  </q-list>
                </q-btn-dropdown>
              </template>
              <!-- big screens -->
              <template v-else>
                <template
                  v-for="(p, index) in pages"
                  :key="index"
                >
                  <q-tab
                    :name="p.name"
                    :label="p.label"
                    @click="page_select(p.link)"
                  />
                </template>
              </template>
            </q-tabs>
          </div>
        </div>
      </template>
    </div>

    <!-- ---------------- Accounting Plan Content ---------------- -->
    <div class="q-px-xl q-py-md q-pt-lg">
      <!-- Display selected tab -->
      <q-tab-panels
        v-model="page_current"
        animated
        class=""
      >
        <!-- AccountingMappings / TransactionKinds -->
        <q-tab-panel
          v-if="page_current == 'accounting_mappings' && accounting_mappings_transaction_kinds_filtered"
          name="accounting_mappings"
          class="q-pa-none"
        >
          <div class="row text-center q-mb-none justify-between items-center">
            <div class="col-shrink align-center content-center">
              <q-btn-toggle
                v-model="accounting_mappings_category_selected"
                unelevated
                color="accounting-plan-header"
                toggle-color="primary"
                text-color="dark-text"
                no-caps
                size="12px"
                :options="accounting_mappings_categories"
              />
            </div>
            <div class="col-auto align-center content-center">
              <!-- Buttons for Grid and List View -->
              <q-btn
                round
                icon="grid_view"
                :color="accounting_mappings_view_selected === 'grid' ? 'accent' : 'primary'"
                flat
                @click="accounting_mappings_view_selected = 'grid'"
              />
              <q-btn
                round
                icon="format_list_bulleted"
                :color="accounting_mappings_view_selected === 'list' ? 'accent' : 'primary'"
                flat
                @click="accounting_mappings_view_selected = 'list'"
              />
            </div>
          </div>

          <div class="q-mt-lg q-pa-none">
            <div
              v-if="accounting_mappings_category_selected == 'accounting_mappings_category_transaction_kinds'"
              class="row q-mt-none items-center justify-between q-mb-lg"
            >
              <div class="col-shrink text-left">
                <span class="text-h6">{{ i18n.accounting_mappings_category_transaction_kinds }}</span>
              </div>
              <div class="col-shrink row items-center">
                <!-- Search transaction kind -->
                <q-input
                  v-model="accounting_mappings_transaction_kinds_search"
                  class="q-mr-md"
                  outlined
                  color="drawer-background"
                  standout
                  hide-bottom-space
                  dense
                  style="max-width: 240px"
                  :placeholder="i18n.search"
                >
                  <template v-slot:prepend>
                    <q-icon name="search" />
                  </template>
                  <template v-slot:append>
                    <q-icon
                      v-if="accounting_mappings_transaction_kinds_search != ''"
                      name="close"
                      class="cursor-pointer"
                      @click="accounting_mappings_transaction_kinds_search = ''"
                    />
                  </template>
                </q-input>

                <!-- Add new transaction kind -->
                <q-btn
                  unelevated
                  color="primary"
                  :label="i18n.operation_type_add"
                  icon="las la-plus"
                  no-caps
                  @click="transaction_kinds_edit({})"
                />
              </div>
            </div>

            <div
              v-for="transaction_kind in accounting_mappings_transaction_kinds_filtered"
              :id="`transaction-kind-${transaction_kind.id}`"
              :key="transaction_kind.id"
              :ref="transaction_kind.label_fr"
              class="q-mb-xl bg-accounting-plan-transaction-kind q-pt-sm"
              style="border-radius: 8px;"
            >
              <!-- Header -->
              <q-card-section class="row items-center justify-space-between q-py-xs">
                <div class="col-auto">
                  <q-item class="q-px-none">
                    <q-item-section avatar>
                      <q-avatar
                        :color="transaction_kind.icon.color"
                        size="32px"
                        class="q-mr-sm"
                      >
                        <q-icon
                          :name="transaction_kind.icon.class"
                          color="black"
                          size="24px"
                        />
                      </q-avatar>
                    </q-item-section>
                    <q-item-section>
                      <q-item-label>
                        <span class="q-card-title q-mr-md">{{ transaction_kind.label_fr }}
                          <q-icon
                            v-if="transaction_kind.calculate_capital_gain_loss"
                            name="las la-calculator"
                            color="text-default"
                            size="14px"
                            class="q-ml-sm"
                          >
                            <q-tooltip :delay="500">{{ i18n.operation_type_capital_gain_lost_description }}</q-tooltip>
                          </q-icon>
                        </span>
                      </q-item-label>
                      <q-item-label
                        caption
                        class="text-light"
                      >
                        {{ transaction_kind.name }}
                      </q-item-label>
                    </q-item-section>
                  </q-item>
                </div>
                <div class="col text-right">
                  <q-btn
                    v-if="!transaction_kind.menu_add_disabled"
                    :disabled="transaction_kind.ignore_transaction"
                    :color="transaction_kind.ignore_transaction ? 'tertiary' : 'primary'"
                    round
                    icon="las la-plus"
                    flat
                    @click="accounting_mappings_dialog_in_or_out_open(transaction_kind, transaction_kind.accounting_mappings.length)"
                  />

                  <q-btn
                    v-if="!transaction_kind.menu_edit_disabled"
                    color="secondary"
                    round
                    icon="more_vert"
                    flat
                    @click.stop
                  >
                    <q-menu>
                      <q-list style="min-width: 220px">
                        <!-- edit -->
                        <q-item
                          v-close-popup
                          clickable
                          @click="transaction_kinds_edit(transaction_kind)"
                        >
                          <q-item-section avatar>
                            <q-icon
                              name="las la-pen"
                              class="q-pr-none"
                            />
                          </q-item-section>
                          <q-item-section>{{ i18n.operation_type_edit }}</q-item-section>
                        </q-item>

                        <!-- Delete -->
                        <q-item
                          v-if="transaction_kind.system_default == false"
                          v-close-popup
                          clickable
                          @click="transaction_kinds_delete_confirm(transaction_kind)"
                        >
                          <q-item-section avatar>
                            <q-icon
                              name="las la-trash-alt"
                              class="q-pr-none"
                            />
                          </q-item-section>
                          <q-item-section>{{ $q.lang.label.remove }}</q-item-section>
                        </q-item>

                        <q-item
                          v-if="transaction_kind.system_default == true"
                          disable
                        >
                          <q-item-section avatar>
                            <q-icon
                              name="las la-trash-alt"
                              class="q-pr-none"
                            />
                          </q-item-section>
                          <q-item-section>{{ $q.lang.label.remove }}</q-item-section>
                          <q-tooltip :delay="500">
                            {{ i18n.operation_type_delete_disabled }}
                          </q-tooltip>
                        </q-item>
                      </q-list>
                    </q-menu>
                  </q-btn>
                </div>
              </q-card-section>

              <q-card-section
                class="row items-start"
                :class="{
                  'q-col-gutter-md': accounting_mappings_view_selected == 'grid',
                  'q-pa-none': accounting_mappings_view_selected == 'list' && transaction_kind.accounting_mappings.length,
                }"
              >
                <!-- Message banner when there is no accounting mappings ( positive or negative ) -->
                <div
                  v-if="
                    transaction_kind.ignore_transaction == false &&
                      transaction_kind.accounting_mappings.length == 0
                  "
                  class="col-xs-12 col-sm-12 col-md-12 col-lg-6"
                >
                  <q-banner
                    class="bg-pastel-blue q-pa-md"
                    dense
                  >
                    <template v-slot:avatar>
                      <q-icon
                        name="las la-info-circle"
                        class="message-box-text"
                        size="sm"
                      />
                    </template>
                    <div class="">
                      {{ i18n.operation_type_empty }}
                    </div>
                  </q-banner>
                </div>

                <!-- Message banner when the transactions are ignored -->
                <div
                  v-if="transaction_kind.ignore_transaction == true"
                  class="col-xs-12 col-sm-12 col-md-12 col-lg-6"
                >
                  <q-banner
                    class="bg-pastel-red-light q-pa-md"
                    dense
                  >
                    <template v-slot:avatar>
                      <q-icon
                        name="las la-ban"
                        class=""
                        size="sm"
                        color="negative"
                      />
                    </template>
                    <div class="">
                      {{ i18n.operation_type_ignored }}
                    </div>
                  </q-banner>
                </div>

                <template v-if="transaction_kind.accounting_mappings.length > 0">
                  <!-- AccountingMappings -->
                  <div
                    v-if="accounting_mappings_view_selected == 'grid'"
                    class="w-full grid gap-2 auto-rows-auto [grid-auto-flow:dense]"
                    :class="{
                      'grid-cols-2': $q.screen.gt.lg && accounting_mappings_category_selected != 'accounting_mappings_category_swap',
                    }"
                  >
                    <template
                      v-for="(am, am_index) in transaction_kind.accounting_mappings"
                      :key="am.id"
                    >
                      <q-card
                        class="accounting-mapping-card-bg accounting-mapping-item"
                        :class="{
                          'col-start-1': am.positive_amount || !$q.screen.gt.lg,
                          'col-start-2': !am.positive_amount && $q.screen.gt.lg,
                        }"
                        flat
                      >
                        <!-- Accounting mapping human readble description - order index -->
                        <q-card-section class="row justify-between">
                          <span>
                            <template v-if="am.for_trade">
                              <q-icon
                                name="mdi-swap-horizontal"
                                class="q-mr-sm"
                                color="primary"
                                size="xs"
                              >
                                <q-tooltip
                                  anchor="center right"
                                  self="center left"
                                  :offset="[10, 10]"
                                  :delay="500"
                                >
                                  {{ i18n.accounting_mapping_swap_transaction }}
                                </q-tooltip>
                              </q-icon>
                            </template>
                            <template v-else>
                              <q-icon
                                v-if="am.positive_amount"
                                name="mdi-transfer-down"
                                class="q-mr-sm"
                                color="positive"
                                size="xs"
                              >
                                <q-tooltip
                                  anchor="center right"
                                  self="center left"
                                  :offset="[10, 10]"
                                  :delay="500"
                                >
                                  {{ i18n.accounting_mapping_incoming_transaction }}
                                </q-tooltip>
                              </q-icon>
                              <q-icon
                                v-else
                                name="mdi-transfer-up"
                                class="q-mr-sm"
                                color="negative"
                                size="xs"
                              >
                                <q-tooltip
                                  anchor="center right"
                                  self="center left"
                                  :offset="[10, 10]"
                                  :delay="500"
                                >
                                  {{ i18n.accounting_mapping_outgoing_transaction }}
                                </q-tooltip>
                              </q-icon>
                            </template>

                            <span
                              class="text-secondary-caption q-mb-none"
                              v-html="am.description"
                            />
                          </span>
                        </q-card-section>

                        <q-separator />

                        <!-- Ecriture lib, Journal, TVA, Base -->
                        <q-card-section class="row items-center justify-space-between q-pb-none">
                          <!-- Ecriture LIB -->
                          <div class="col-auto">
                            <q-item>
                              <q-item-section>
                                <q-item-label
                                  caption
                                  class="text-muted"
                                >
                                  {{ i18n.accounting_mapping_description }}
                                </q-item-label>
                                <q-item-label class="text-weight-medium">
                                  {{ am.ecriture_lib }}
                                </q-item-label>
                              </q-item-section>
                            </q-item>
                          </div>

                          <!-- JOURNAL -->
                          <div class="col-auto">
                            <q-item>
                              <q-item-section>
                                <q-item-label
                                  caption
                                  class="text-muted"
                                >
                                  {{ i18n.accounting_mapping_journal }}
                                </q-item-label>
                                <q-item-label class="text-weight-medium">
                                  {{ am.accounting_journal_label }}
                                </q-item-label>
                              </q-item-section>
                            </q-item>
                          </div>

                          <!-- TVA -->
                          <div class="col-grow text-right">
                            <q-item>
                              <q-item-section>
                                <q-item-label
                                  caption
                                  class="text-muted"
                                >
                                  {{ i18n.accounting_mapping_vat }}
                                </q-item-label>
                                <q-item-label class="text-weight-medium">
                                  {{ am.tax_percent }}%
                                </q-item-label>
                              </q-item-section>
                            </q-item>
                          </div>

                          <!-- Base de calcul -->
                          <div class="col-shrink text-right">
                            <q-item>
                              <q-item-section>
                                <q-item-label
                                  caption
                                  class="text-muted"
                                >
                                  {{ i18n.accounting_mapping_base }}
                                </q-item-label>
                                <q-item-label class="text-weight-medium">
                                  {{ am.base_calculation_label }}
                                </q-item-label>
                              </q-item-section>
                            </q-item>
                          </div>
                        </q-card-section>

                        <!-- DEBIT / CREDIT -->
                        <q-card-section class="q-pa-md">
                          <div class="row full-width items-center accounting-mapping-flow-bg rounded-borders">
                            <!-- Debit account -->
                            <div class="col-5">
                              <q-item class="q-pr-none">
                                <q-item-section>
                                  <q-item-label
                                    caption
                                    class="text-muted"
                                  >
                                    {{ i18n.accounting_mapping_debit_account }}
                                  </q-item-label>
                                  <q-item-label class="text-bold">
                                    {{ am.debit_accounting_account_label }}
                                  </q-item-label>
                                </q-item-section>
                              </q-item>
                            </div>
                            <!-- Arrow and transaction amount -->
                            <div class="col-2">
                              <div class="row items-center justify-center q-mt-neg-md">
                                <q-badge
                                  v-if="am.total_amount_percent != 100"
                                  :class="'bg-' + percent_color(am.total_amount_percent)"
                                >
                                  <span>{{ am.total_amount_percent + "%" }} </span> <q-icon
                                    class="q-ml-xs"
                                    size="xs"
                                    name="las la-arrow-right"
                                  />
                                </q-badge>

                                <q-icon
                                  v-else
                                  size="xs"
                                  name="las la-arrow-right"
                                />
                              </div>
                            </div>

                            <!-- Credit account -->
                            <div class="col-5 text-right">
                              <q-item class="q-pl-none">
                                <q-item-section>
                                  <q-item-label
                                    caption
                                    class="text-muted"
                                  >
                                    {{ i18n.accounting_mapping_credit_account }}
                                  </q-item-label>
                                  <q-item-label class="text-bold">
                                    {{ am.credit_accounting_account_label }}
                                  </q-item-label>
                                </q-item-section>
                              </q-item>
                            </div>
                          </div>
                        </q-card-section>

                        <q-separator inset />

                        <!-- Actions -->
                        <q-card-actions class="q-px-md justify-end">
                          <span v-if="am.calculate_capital_gain_loss"> {{ i18n.accounting_mapping_capital_gain_loss }} </span>

                          <q-space />

                          <div class="accounting-mapping-actions">
                            <q-btn
                              v-if="!am.for_trade"
                              unelevated
                              color="secondary"
                              flat
                              size="sm"
                              round
                              icon="las la-copy"
                              no-caps
                              @click="accounting_mappings_duplicate(am, transaction_kind.accounting_mappings.length, transaction_kind)"
                            >
                              <q-tooltip>{{ i18n.duplicate }}</q-tooltip>
                            </q-btn>

                            <q-btn
                              unelevated
                              color="secondary"
                              flat
                              size="sm"
                              round
                              icon="las la-pen"
                              no-caps
                              @click="accounting_mappings_edit(am, am_index, transaction_kind)"
                            >
                              <q-tooltip>{{ i18n.edit }}</q-tooltip>
                            </q-btn>

                            <q-btn
                              v-if="!am.for_trade"
                              unelevated
                              color="secondary"
                              flat
                              size="sm"
                              round
                              icon="las la-trash-alt"
                              no-caps
                              @click="accounting_mappings_delete_confirm(am)"
                            >
                              <q-tooltip>{{ $q.lang.label.remove }}</q-tooltip>
                            </q-btn>
                          </div>
                        </q-card-actions>
                      </q-card>
                    </template>
                  </div>

                  <template v-if="accounting_mappings_view_selected == 'list'">
                    <q-table
                      v-if="transaction_kind.accounting_mappings.length"
                      :rows="transaction_kind.accounting_mappings"
                      :columns="accounting_mappings_list_columns"
                      row-key="id"
                      flat
                      separator="none"
                      class="full-width"
                      dense
                      wrap-cells
                      style="border-radius: inherit"
                      hide-bottom
                      :pagination="{
                        rowsPerPage: -1,
                        descending: false,
                      }"
                    >
                      <template v-slot:body="props">
                        <q-tr
                          :props="props"
                          class="show-accounting-mapping-actions-btns"
                        >
                          <!-- Icon Column -->
                          <q-td>
                            <template v-if="props.row.for_trade">
                              <q-icon
                                name="mdi-swap-horizontal"
                                color="primary"
                              />
                            </template>
                            <template v-else>
                              <template v-if="props.row.positive_amount">
                                <q-icon
                                  name="mdi-transfer-down"
                                  color="positive"
                                />
                              </template>
                              <template v-else>
                                <q-icon
                                  name="mdi-transfer-up"
                                  color="negative"
                                />
                              </template>
                            </template>
                          </q-td>

                          <!-- Debit Account Column -->
                          <q-td class="text-bold">
                            {{ props.row.debit_accounting_account_label }}
                          </q-td>

                          <!-- Credit Account Column -->
                          <q-td class="text-bold">
                            {{ props.row.credit_accounting_account_label }}
                          </q-td>

                          <!-- Journal Column -->
                          <q-td>
                            {{ props.row.accounting_journal_label }}
                          </q-td>

                          <!-- Ecriture Lib Column -->
                          <q-td>
                            {{ props.row.ecriture_lib }}
                          </q-td>

                          <!-- Total Amount Percent Column -->
                          <q-td>
                            {{ props.row.total_amount_percent }}%
                          </q-td>

                          <!-- Tax Percent Column -->
                          <q-td>
                            {{ props.row.tax_percent }}%
                          </q-td>

                          <!-- Is Tax Column -->
                          <q-td>
                            {{ props.row.base_calculation_label }}
                          </q-td>

                          <!-- Actions Column -->
                          <q-td
                            style="text-align: right"
                            class="accounting-mapping-actions"
                          >
                            <q-btn
                              v-if="!props.row.for_trade"
                              unelevated
                              color="secondary"
                              flat
                              size="sm"
                              round
                              icon="las la-copy"
                              no-caps
                              @click="accounting_mappings_duplicate(props.row, transaction_kind.accounting_mappings.length, transaction_kind)"
                            >
                              <q-tooltip>{{ i18n.duplicate }}</q-tooltip>
                            </q-btn>

                            <q-btn
                              unelevated
                              color="secondary"
                              flat
                              size="sm"
                              round
                              icon="las la-pen"
                              no-caps
                              @click="accounting_mappings_edit(props.row, props.rowIndex, transaction_kind)"
                            >
                              <q-tooltip>{{ i18n.edit }}</q-tooltip>
                            </q-btn>

                            <q-btn
                              v-if="!props.row.for_trade"
                              unelevated
                              color="secondary"
                              flat
                              size="sm"
                              round
                              icon="las la-trash-alt"
                              no-caps
                              @click="accounting_mappings_delete_confirm(props.row)"
                            >
                              <q-tooltip>{{ $q.lang.label.remove }}</q-tooltip>
                            </q-btn>
                          </q-td>
                        </q-tr>
                      </template>
                    </q-table>
                  </template>
                </template>
              </q-card-section>
            </div>

            <!-- Search returns no results -->
            <q-banner
              v-if="accounting_mappings_transaction_kinds_search != '' && accounting_mappings_transaction_kinds_filtered.length == 0"
              class="bg-pastel-orange q-pa-md"
              dense
              style="width: fit-content; margin: auto"
            >
              <template v-slot:avatar>
                <q-icon
                  name="las la-exclamation-circle"
                  size="md"
                />
              </template>
              {{ $q.lang.table.noResults }} ({{ accounting_mappings_transaction_kinds_search }})
            </q-banner>
          </div>

          <!-- TransactionKinds - Create / Edit -->
          <q-dialog v-model="transaction_kinds_dialog_show">
            <q-card class="q-pa-md">
              <q-card-section>
                <div class="text-h6">
                  {{ transaction_kinds_dialog_title }}
                </div>
              </q-card-section>

              <q-card-section class="row q-col-gutter-md">
                <!-- Transaction Kind Label FR -->
                <div class="col-12">
                  <span class="text-input-label">{{ i18n.operation_type_label }}</span>
                  <q-input
                    v-model="transaction_kinds_dialog_form.label_fr"
                    :autofocus="!transaction_kinds_dialog_form.id"
                    outlined
                    dense
                    :readonly="transaction_kinds_dialog_form.system_default"
                    class="full-width q-mt-sm"
                    :rules="[(val) => val?.trim() !== '' || i18n.required]"
                  />
                </div>

                <!-- Transaction Kind Description -->
                <div class="col-12">
                  <span class="text-input-label">{{ i18n.operation_type_description }}</span>
                  <q-input
                    v-model="transaction_kinds_dialog_form.description"
                    outlined
                    dense
                    :readonly="transaction_kinds_dialog_form.system_default"
                    class="full-width q-mt-sm"
                    :rules="[(val) => val?.trim() !== '' || i18n.required]"
                  />
                </div>

                <!-- Transaction Kind Category -->
                <div class="col-12">
                  <span class="text-input-label">{{ i18n.operation_type_category }}</span>
                  <q-select
                    v-model="transaction_kinds_dialog_form.transaction_kind_category_id"
                    :options="transaction_kind_categories"
                    option-value="id"
                    option-label="label_fr"
                    outlined
                    dense
                    :readonly="transaction_kinds_dialog_form.system_default"
                    class="full-width q-mt-sm"
                    :rules="[(val) => val !== null || i18n.required]"
                    map-options
                    emit-value
                  />
                </div>

                <!-- Ignore transactions -->
                <div class="col-12">
                  <q-item class="q-px-none">
                    <q-item-section avatar>
                      <q-avatar color="pastel-red-light">
                        <q-icon
                          name="las la-ban"
                          color="negative"
                          size="24px"
                        />
                      </q-avatar>
                    </q-item-section>
                    <q-item-section>
                      <q-item-label class="text-input-label q-mb-xs">
                        {{ i18n.operation_type_ignore_transaction }}
                      </q-item-label>
                      <q-item-label caption>
                        {{ i18n.operation_type_ignore_transaction_note }}
                      </q-item-label>
                    </q-item-section>
                    <q-item-section avatar>
                      <q-toggle
                        v-model="transaction_kinds_dialog_form.ignore_transaction"
                        color="accent"
                        :disable="transaction_kinds_dialog_form.system_default"
                      />
                    </q-item-section>
                  </q-item>
                </div>

                <!-- Calculate capital gain loss toggle -->
                <div class="col-12">
                  <q-item class="q-px-none">
                    <q-item-section avatar>
                      <q-avatar color="transparent">
                        <q-icon
                          name="las la-calculator"
                          color="primary"
                          size="24px"
                        />
                      </q-avatar>
                    </q-item-section>
                    <q-item-section>
                      <q-item-label class="text-input-label q-mb-xs">
                        {{ i18n.operation_type_capital_gain_lost }}
                      </q-item-label>
                      <q-item-label caption>
                        {{ i18n.operation_type_capital_gain_lost_description }}
                      </q-item-label>
                    </q-item-section>
                    <q-item-section avatar>
                      <q-toggle
                        v-model="transaction_kinds_dialog_form.calculate_capital_gain_loss"
                        color="accent"
                      />
                    </q-item-section>
                  </q-item>
                </div>
              </q-card-section>

              <q-card-actions
                align="right"
                class="q-mt-md"
              >
                <q-btn
                  :label="$q.lang.label.cancel"
                  color="secondary"
                  flat
                  @click="transaction_kinds_dialog_show = false"
                />
                <q-btn
                  :label="transaction_kinds_dialog_form.id ? i18n.edit : $q.lang.label.create"
                  color="primary"
                  unelevated
                  :disable="transaction_kinds_dialog_save_disabled"
                  @click="transaction_kinds_dialog_submit()"
                />
              </q-card-actions>
              <q-inner-loading :showing="transaction_kinds_dialog_loading">
                <q-spinner-tail
                  size="50px"
                  color="primary"
                />
              </q-inner-loading>
            </q-card>
          </q-dialog>

          <!-- AccountingMappings - Create / Edit / Duplicate -->
          <!-- Dialog In or Out -->
          <q-dialog v-model="accounting_mappings_dialog_in_or_out_show">
            <div
              class="q-pa-lg card-background"
              style="width: 560px; max-width: 80vw"
            >
              <q-item
                v-ripple
                clickable
                class="q-card dotted-card q-pa-md no-shadow q-mb-md"
                @click="accounting_mappings_add('in')"
              >
                <q-item-section avatar>
                  <q-icon
                    name="mdi-transfer-down"
                    size="sm"
                    color="positive"
                  />
                </q-item-section>
                <q-item-section>
                  <q-item-label><span v-html="i18n.accounting_mapping_incoming_transaction_enter" /></q-item-label>
                </q-item-section>
                <q-item-section side>
                  <q-icon name="las la-angle-right" />
                </q-item-section>
              </q-item>

              <q-item
                v-ripple
                clickable
                class="q-card dotted-card q-pa-md no-shadow"
                @click="accounting_mappings_add('out')"
              >
                <q-item-section avatar>
                  <q-icon
                    name="mdi-transfer-up"
                    size="sm"
                    color="negative"
                  />
                </q-item-section>
                <q-item-section>
                  <q-item-label><span v-html="i18n.accounting_mapping_outgoing_transaction_enter" /></q-item-label>
                </q-item-section>
                <q-item-section side>
                  <q-icon name="las la-angle-right" />
                </q-item-section>
              </q-item>
            </div>
          </q-dialog>

          <!-- AccountingMapping Dialog -->
          <!-- v-if + v-model is used to ensure the dialog resets its state every time it opens -->
          <q-dialog
            v-if="accounting_mappings_dialog_show"
            v-model="accounting_mappings_dialog_show"
            maximized
            full-width
          >
            <q-layout
              view="hHh lpR fFf"
              container
              class="bg-page"
            >
              <q-page-container>
                <q-page class="centered-container-medium column full-height q-pa-xl">
                  <!-- Header -->
                  <div class="row q-pt-xl">
                    <div class="col-12 text-center q-mb-md">
                      <span
                        v-if="accounting_mappings_dialog_form.positive_amount"
                        class="text-h6"
                      >{{ accounting_mappings_dialog_title_in }}</span>
                      <span
                        v-else
                        class="text-h6"
                      >{{ accounting_mappings_dialog_title_out }}</span>
                    </div>

                    <div class="col-12 text-center q-pb-lg">
                      <span class="text-subtitle2">
                        <span class="text-bold q-mr-md text-muted"> {{ accounting_mappings_dialog_form.transaction_kind.label_fr }}</span>
                        <span
                          v-if="accounting_mappings_dialog_form.transaction_kind_category_label"
                          class="accounting-mapping-card-bg q-pa-xs q-px-sm"
                          :class="[
                            'bg-' + accounting_mappings_dialog_form.transaction_kind.icon.color
                          ]"
                          style="border-radius: 8px; font-size: 12px;"
                        >
                          {{ accounting_mappings_dialog_form.transaction_kind_category_label }}
                        </span>
                      </span>
                    </div>
                  </div>

                  <!-- AccountingMapping form -->
                  <div class="row q-pt-xl">
                    <q-card class="fit q-pa-md">
                      <!-- Positive / Negative amount -->
                      <div
                        class="absolute"
                        style="position: absolute; left: calc(50% - 34px); top: -34px"
                      >
                        <q-btn
                          v-if="accounting_mappings_dialog_form.transaction_kind.id == 'swap'"
                          :unelevated="false"
                          round
                          style="pointer-events: none; z-index: 999"
                          icon="mdi-swap-horizontal"
                          color="primary"
                          size="lg"
                        />
                        <transition
                          v-else
                          appear
                          mode="out-in"
                          enter-active-class="flip-in-x"
                          leave-active-class="flip-out-x"
                        >
                          <q-btn
                            :key="accounting_mappings_dialog_form.positive_amount"
                            :unelevated="false"
                            round
                            style="z-index: 999"
                            :icon="accounting_mappings_dialog_form.positive_amount ? 'mdi-transfer-down' : 'mdi-transfer-up'"
                            :color="accounting_mappings_dialog_form.positive_amount ? 'positive' : 'negative'"
                            size="lg"
                            @click="accounting_mappings_dialog_form.positive_amount = !accounting_mappings_dialog_form.positive_amount"
                          >
                            <!-- badge with a pen icon to indicate it's a button -->
                            <q-icon
                              style="bottom: 0px; right: -20px; z-index: 999"
                              size="xs"
                              name="las la-pen"
                              class="text-muted absolute"
                            />
                          </q-btn>
                        </transition>
                      </div>

                      <!-- Basic inputs -->
                      <q-card-section class="row q-col-gutter-md q-pt-xl">
                        <!-- Ecriture LIB -->
                        <div class="col-12 col-sm-6">
                          <span class="text-input-label">{{ i18n.accounting_mapping_description }}</span>
                          <q-input
                            v-model="accounting_mappings_dialog_form.ecriture_lib"
                            :autofocus="accounting_mappings_dialog_form.ecriture_lib === ''"
                            outlined
                            dense
                            class="full-width q-mt-sm"
                            :rules="[(val) => val?.trim() !== '' || i18n.required]"
                          />
                        </div>

                        <!-- Journal -->
                        <div class="col-12 col-sm-6">
                          <span class="text-input-label">{{ i18n.accounting_mapping_journal }}</span>
                          <q-select
                            v-model="accounting_mappings_dialog_form.accounting_journal_id"
                            outlined
                            dense
                            class="full-width q-mt-sm"
                            :options="accounting_mappings_dialog_accounting_journals"
                            option-value="id"
                            option-label="label"
                            :rules="[(val) => val !== null || i18n.required]"
                            emit-value
                            map-options
                            fill-input
                            use-input
                            hide-selected
                            input-debounce="0"
                            @filter="accounting_mappings_dialog_accounting_journals_filter"
                          >
                            <template v-slot:no-option>
                              <q-item>
                                <q-item-section class="text-muted">
                                  {{ $q.lang.table.noResults }}
                                </q-item-section>
                              </q-item>
                            </template>
                          </q-select>
                        </div>

                        <!-- Debit account -->
                        <div class="col-12 col-sm-6">
                          <span class="text-input-label">{{ i18n.accounting_mapping_debit_account }}</span>
                          <q-select
                            v-model="accounting_mappings_dialog_form.debit_accounting_account_id"
                            outlined
                            dense
                            class="full-width q-mt-sm"
                            :options="accounting_mappings_dialog_accounting_accounts"
                            option-value="id"
                            option-label="label"
                            :rules="[(val) => val !== null || i18n.required]"
                            emit-value
                            map-options
                            fill-input
                            use-input
                            hide-selected
                            input-debounce="0"
                            @filter="accounting_mappings_dialog_accounting_accounts_filter"
                          >
                            <template v-slot:no-option>
                              <q-item>
                                <q-item-section class="text-muted">
                                  {{ $q.lang.table.noResults }}
                                </q-item-section>
                              </q-item>
                            </template>
                          </q-select>
                        </div>

                        <!-- Credit account -->
                        <div class="col-12 col-sm-6">
                          <span class="text-input-label">{{ i18n.accounting_mapping_credit_account }}</span>
                          <q-select
                            v-model="accounting_mappings_dialog_form.credit_accounting_account_id"
                            outlined
                            dense
                            class="full-width q-mt-sm"
                            :options="accounting_mappings_dialog_accounting_accounts"
                            option-value="id"
                            option-label="label"
                            :rules="[(val) => val !== null || i18n.required]"
                            emit-value
                            map-options
                            fill-input
                            use-input
                            hide-selected
                            input-debounce="0"
                            @filter="accounting_mappings_dialog_accounting_accounts_filter"
                          >
                            <template v-slot:no-option>
                              <q-item>
                                <q-item-section class="text-muted">
                                  {{ $q.lang.table.noResults }}
                                </q-item-section>
                              </q-item>
                            </template>
                          </q-select>
                        </div>

                        <p
                          class="q-py-none link-style text-primary"
                          @click="accounting_mappings_dialog_form_inverse_accounts"
                        >
                          <q-icon
                            name="las la-exchange-alt"
                            class="q-mr-sm"
                          />
                          {{ i18n.accounting_mapping_switch_accounts }}
                        </p>
                      </q-card-section>

                      <!-- Splitting -->
                      <q-card-section>
                        <!-- Base amount -->
                        <span class="text-input-label">{{ i18n.accounting_mapping_base_amount }}</span>
                        <div class="q-gutter-sm q-pt-sm q-mb-lg">
                          <q-radio
                            v-model="accounting_mappings_dialog_form.amount_at_wac"
                            :val="false"
                            :label="i18n.accounting_mapping_fiat_amount_at_market_value"
                          />
                          <q-radio
                            v-model="accounting_mappings_dialog_form.amount_at_wac"
                            :val="true"
                            :label="i18n.accounting_mapping_fiat_amount_at_weighted_cost"
                          />
                        </div>


                        <!-- Amount split -->
                        <span class="text-input-label">{{ i18n.accounting_mapping_amount_split }}</span>
                        <div class="q-gutter-sm q-pt-sm">
                          <q-radio
                            v-model="accounting_mappings_dialog_form.is_amount_splitted"
                            :val="false"
                            :label="i18n.accounting_mapping_amount_split_all"
                          />
                          <q-radio
                            v-model="accounting_mappings_dialog_form.is_amount_splitted"
                            :val="true"
                            :label="i18n.accounting_mapping_amount_split_partial"
                          />
                        </div>


                        <!-- Show progress bar and amount split input if amount is splitted -->
                        <div v-if="accounting_mappings_dialog_form.is_amount_splitted">
                          <!-- Progress bar -->
                          <div class="row items-center q-col-gutter-md">
                            <div class="col-9">
                              <q-linear-progress
                                stripe
                                rounded
                                size="20px"
                                :value="accounting_mappings_dialog_form.total_amount_percent * 0.01"
                                :color="percent_color(accounting_mappings_dialog_form.total_amount_percent)"
                              />
                            </div>
                            <div class="col-3 text-right">
                              <q-input
                                v-model.number="accounting_mappings_dialog_form.total_amount_percent"
                                outlined
                                dense
                                standout
                                hide-bottom-space
                                :placeholder="i18n.accounting_mapping_percentage_placeholder"
                                type="number"
                                :rules="[
                                  (val) => val > 0 || i18n.accounting_mapping_percentage_must_greater_than_zero,
                                  (val) => val <= 100 || i18n.accounting_mapping_percentage_must_less_than_hundred,
                                ]"
                              >
                                <template v-slot:append>
                                  <q-icon name="las la-percent" />
                                </template>
                              </q-input>
                            </div>
                          </div>
                        </div>
                      </q-card-section>

                      <!-- TVA -->
                      <q-card-section class="row q-col-gutter-md">
                        <div class="col-12">
                          <q-toggle
                            v-model="accounting_mappings_dialog_form.show_tva_settings"
                            label="TVA"
                            color="accent"
                            left-label
                            class="text-input-label"
                          />
                        </div>
                        <template v-if="accounting_mappings_dialog_form.show_tva_settings">
                          <!-- TVA -->
                          <div class="col-12 col-sm-6">
                            <span class="text-input-label">
                              {{
                                accounting_mappings_dialog_form.is_amount_splitted ?
                                  i18n.accounting_mapping_vat_rate_partial :
                                  i18n.accounting_mapping_vat_rate
                              }}
                            </span>
                            <q-input
                              v-model.number="accounting_mappings_dialog_form.tax_percent"
                              outlined
                              dense
                              standout
                              hide-bottom-space
                              :placeholder="i18n.accounting_mapping_percentage_placeholder"
                              type="number"
                              class="q-mt-sm"
                              :rules="[
                                (val) => val >= 0 || i18n.accounting_mapping_percentage_must_be_positive,
                                (val) => val <= 100 || i18n.accounting_mapping_percentage_must_less_than_hundred,
                              ]"
                            >
                              <template v-slot:append>
                                <q-icon name="las la-percent" />
                              </template>
                            </q-input>
                          </div>

                          <div
                            v-if="accounting_mappings_dialog_form.tax_percent > 0"
                            class="col-12 col-sm-6"
                          >
                            <span class="text-input-label">{{ i18n.accounting_mapping_percentage_type }}</span>
                            <div class="q-gutter-sm q-pt-sm">
                              <q-radio
                                v-model="accounting_mappings_dialog_form.is_tax"
                                :val="false"
                                :label="i18n.accounting_mapping_amount_ti"
                              />
                              <q-radio
                                v-model="accounting_mappings_dialog_form.is_tax"
                                :val="true"
                                :label="i18n.accounting_mapping_amount_tax"
                              />
                            </div>
                          </div>
                        </template>
                      </q-card-section>

                      <q-separator />

                      <!-- Cancel - Validate -->
                      <q-card-actions
                        align="right"
                        class="q-mt-md"
                      >
                        <q-btn
                          :label="$q.lang.label.cancel"
                          color="secondary"
                          flat
                          @click="accounting_mappings_dialog_show = false"
                        />
                        <q-btn
                          :label="accounting_mappings_dialog_form.id ? i18n.edit : $q.lang.label.create"
                          color="primary"
                          unelevated
                          :disable="accounting_mappings_dialog_form.ecriture_lib === '' ||
                            accounting_mappings_dialog_form.accounting_journal_id === null ||
                            accounting_mappings_dialog_form.debit_accounting_account_id === null ||
                            accounting_mappings_dialog_form.credit_accounting_account_id === null
                          "
                          @click="accounting_mappings_dialog_submit"
                        />
                      </q-card-actions>
                    </q-card>
                  </div>

                  <q-footer
                    flat
                    bordered
                    class="card-background text-center items-center"
                  >
                    <div class="row full-width no-wrap justify-center q-my-md items-center">
                      <span class=" text-subtitle2 text-bold items-center">
                        {{ i18n.accounting_mapping_accounting_plan }} {{ accounting_plan.name }}
                      </span>
                    </div>
                  </q-footer>
                </q-page>
              </q-page-container>
            </q-layout>

            <!-- AccountingMapping inner loading -->
            <q-inner-loading :showing="editAccountingMappingLoading">
              <q-spinner-tail
                size="50px"
                color="primary"
              />
            </q-inner-loading>
          </q-dialog>
        </q-tab-panel>

        <!-- AccountingAccounts -->
        <q-tab-panel
          v-if="page_current == 'accounting_accounts' && accounting_accounts_filtered"
          name="accounting_accounts"
          class="q-pa-none"
        >
          <div class="row q-mt-none items-center justify-between q-mb-lg">
            <div class="col-shrink text-left">
              <span class="text-h6">{{ pages[1]?.label }}</span>
            </div>
            <div class="col-shrink row items-center">
              <!-- Search -->
              <q-input
                v-model="accounting_accounts_search"
                class="q-mr-md"
                outlined
                color="drawer-background"
                standout
                hide-bottom-space
                dense
                style="max-width: 240px"
                :placeholder="i18n.search"
              >
                <template v-slot:prepend>
                  <q-icon name="search" />
                </template>
                <template v-slot:append>
                  <q-icon
                    v-if="accounting_accounts_search != ''"
                    name="close"
                    class="cursor-pointer"
                    @click="accounting_accounts_search = ''"
                  />
                </template>
              </q-input>

              <!-- Add new  -->
              <q-btn
                unelevated
                color="primary"
                :label="i18n.account_add"
                icon="las la-plus"
                no-caps
                @click="accounting_accounts_edit({})"
              />
            </div>
          </div>

          <!-- List -->
          <q-table
            :rows="accounting_accounts_filtered"
            :columns="accounting_accounts_columns"
            row-key="id"
            flat
            separator="none"
            :pagination="{
              rowsPerPage: -1,
              sortBy: 'account_num',
              descending: false,
            }"
            hide-bottom
            class="q-mb-xl"
          >
            <template v-slot:body-cell-account_num="props">
              <q-td :props="props">
                <span class="text-bold">{{ props.row.account_num }}</span>
              </q-td>
            </template>
            <template v-slot:body-cell-actions="props">
              <q-td :props="props">
                <q-btn
                  unelevated
                  color="secondary"
                  flat
                  size="sm"
                  round
                  icon="las la-pen"
                  class="q-mr-sm"
                  @click="accounting_accounts_edit(props.row)"
                />
                <q-btn
                  unelevated
                  color="negative"
                  flat
                  size="sm"
                  round
                  icon="delete"
                  @click="accounting_accounts_delete_confirm(props.row)"
                />
              </q-td>
            </template>
          </q-table>

          <!-- AccountingAccout Dialog - Edit / Create -->
          <q-dialog v-model="accounting_accounts_dialog_show">
            <q-card class="q-pa-md">
              <q-card-section>
                <div class="text-h6">
                  {{ accounting_accounts_dialog_title }}
                </div>
                <p
                  class="q-mt-md"
                  v-html="i18n.interpolation_instructions"
                />
                <p class="text-caption q-mb-none">
                  {{ i18n.interpolation_example }}
                </p>
              </q-card-section>

              <q-card-section class="row q-col-gutter-md">
                <!-- Numéro de compte -->
                <div class="col-12">
                  <span class="text-input-label">{{ i18n.account_number }}</span>
                  <q-input
                    v-model="accounting_accounts_dialog_form.account_num"
                    outlined
                    dense
                    class="full-width q-mt-sm"
                    :rules="[(val) => val?.trim() !== '' || i18n.required]"
                  />
                </div>

                <!-- Account Lib -->
                <div class="col-12">
                  <span class="text-input-label">{{ i18n.account_lib }}</span>
                  <q-input
                    v-model="accounting_accounts_dialog_form.account_lib"
                    outlined
                    dense
                    class="full-width q-mt-sm"
                    :rules="[(val) => val?.trim() !== '' || i18n.required]"
                  />
                </div>
              </q-card-section>

              <q-card-actions
                align="right"
                class="q-mt-md"
              >
                <q-btn
                  :label="$q.lang.label.cancel"
                  color="secondary"
                  flat
                  @click="accounting_accounts_dialog_show = false"
                />
                <q-btn
                  :label="i18n.submit"
                  color="primary"
                  unelevated
                  :disable="accounting_accounts_dialog_save_disabled"
                  @click="accounting_accounts_dialog_submit()"
                />
              </q-card-actions>
            </q-card>
          </q-dialog>
        </q-tab-panel>

        <!-- AccountingJournals -->
        <q-tab-panel
          v-if="page_current == 'accounting_journals' && accounting_journals_filtered"
          name="accounting_journals"
          class="q-pa-none"
        >
          <div class="row q-mt-none items-center justify-between q-mb-lg">
            <div class="col-shrink text-left">
              <span class="text-h6">{{ pages[2]?.label }}</span>
            </div>
            <div class="col-shrink row items-center">
              <!-- Search -->
              <q-input
                v-model="accounting_journals_search"
                class="q-mr-md"
                outlined
                color="drawer-background"
                standout
                hide-bottom-space
                dense
                style="max-width: 240px"
                :placeholder="i18n.search"
              >
                <template v-slot:prepend>
                  <q-icon name="search" />
                </template>
                <template v-slot:append>
                  <q-icon
                    v-if="accounting_journals_search != ''"
                    name="close"
                    class="cursor-pointer"
                    @click="accounting_journals_search = ''"
                  />
                </template>
              </q-input>

              <!-- Add new  -->
              <q-btn
                unelevated
                color="primary"
                :label="i18n.journal_add"
                icon="las la-plus"
                no-caps
                @click="accounting_journals_edit({})"
              />
            </div>
          </div>

          <!-- List -->
          <q-table
            :rows="accounting_journals_filtered"
            :columns="accounting_journals_columns"
            row-key="id"
            flat
            separator="none"
            :pagination="{
              rowsPerPage: -1,
              sortBy: 'journal_lib',
              descending: false,
            }"
            hide-bottom
            class="q-mb-xl"
          >
            <template v-slot:body-cell-journal_lib="props">
              <q-td :props="props">
                <span class="text-bold">{{ props.row.journal_lib }}</span>
              </q-td>
            </template>
            <template v-slot:body-cell-actions="props">
              <q-td :props="props">
                <q-btn
                  unelevated
                  color="secondary"
                  flat
                  size="sm"
                  round
                  icon="las la-pen"
                  class="q-mr-sm"
                  @click="accounting_journals_edit(props.row)"
                />
                <q-btn
                  unelevated
                  color="negative"
                  flat
                  size="sm"
                  round
                  icon="delete"
                  @click="accounting_journals_delete_confirm(props.row)"
                />
              </q-td>
            </template>
          </q-table>

          <!-- AccountingJournal Dialog - Edit / Create -->
          <q-dialog v-model="accounting_journals_dialog_show">
            <q-card class="q-pa-md">
              <q-card-section>
                <div class="text-h6">
                  {{ accounting_journals_dialog_title }}
                </div>
              </q-card-section>

              <q-card-section class="row q-col-gutter-md">
                <!-- Journal Code -->
                <div class="col-12">
                  <span class="text-input-label">{{ i18n.exchange_accounts_journal_code }}</span>
                  <q-input
                    v-model="accounting_journals_dialog_form.journal_code"
                    outlined
                    dense
                    class="full-width q-mt-sm"
                    :rules="[(val) => (val?.trim() !== '') || i18n.required]"
                  />
                </div>

                <!-- Journal Lib -->
                <div class="col-12">
                  <span class="text-input-label">{{ i18n.exchange_accounts_journal_lib }}</span>
                  <q-input
                    v-model="accounting_journals_dialog_form.journal_lib"
                    outlined
                    dense
                    class="full-width q-mt-sm"
                    :rules="[(val) => (val?.trim() !== '') || i18n.required]"
                  />
                </div>
              </q-card-section>

              <q-card-actions
                align="right"
                class="q-mt-md"
              >
                <q-btn
                  :label="$q.lang.label.cancel"
                  color="secondary"
                  flat
                  @click="accounting_journals_dialog_show = false"
                />
                <q-btn
                  :label="i18n.submit"
                  color="primary"
                  unelevated
                  :disable="accounting_journals_dialog_save_disabled"
                  @click="accounting_journals_dialog_submit()"
                />
              </q-card-actions>
            </q-card>
          </q-dialog>
        </q-tab-panel>

        <!-- AccountingAssetMappings -->
        <q-tab-panel
          v-if="page_current == 'accounting_asset_mappings' && accounting_asset_mappings.data"
          name="accounting_asset_mappings"
          class="q-pa-none"
        >
          <div class="row q-mt-none items-center justify-between q-mb-lg">
            <div class="col-shrink text-left">
              <span class="text-h6">{{ i18n.accounting_asset_mappings_title }}</span>
            </div>
            <div class="col-shrink row items-center">
              <!-- AccountingAssetMappings Search -->
              <q-input
                v-model="accounting_asset_mappings_search"
                debounce="500"
                class="q-mr-md"
                outlined
                color="drawer-background"
                standout
                hide-bottom-space
                dense
                style="max-width: 240px"
                :placeholder="i18n.search"
              >
                <template v-slot:prepend>
                  <q-icon name="search" />
                </template>
                <template v-slot:append>
                  <q-icon
                    v-if="accounting_asset_mappings_search != ''"
                    name="close"
                    class="cursor-pointer"
                    @click="accounting_asset_mappings_search = ''"
                  />
                </template>
              </q-input>
            </div>
          </div>

          <!-- AccountingAssetMappings mappings -->
          <q-table
            v-if="accounting_asset_mappings.data.length > 0"
            v-model:pagination="accounting_asset_mappings_table_pagination"
            :rows="accounting_asset_mappings.data"
            :columns="accounting_asset_mappings_columns"
            :loading="accounting_asset_mappings_table_loading"
            row-key="id"
            flat
            separator="none"
            class="q-mb-xl accounting_asset_mappings_table"
            hide-bottom
          >
            <template v-slot:body-cell-asset_symbol="props">
              <q-td :props="props">
                <q-item
                  dense
                  class="q-pa-none"
                >
                  <q-item-section avatar>
                    <q-avatar size="sm">
                      <img
                        :alt="props.row.asset_symbol"
                        width="14"
                        height="14"
                        :src="props.row.asset_logo_url"
                        @error="(event) => { event.target.style.display = 'none'; }"
                      >
                    </q-avatar>
                  </q-item-section>

                  <q-item-section>
                    <q-item-label
                      lines="1"
                      class="text-bold"
                    >
                      {{ props.row.asset_symbol }}
                    </q-item-label>
                    <q-item-label
                      caption
                      lines="1"
                    >
                      {{ props.row.asset_name }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </q-td>
            </template>

            <template v-slot:body-cell-code="props">
              <q-td :props="props">
                <span class="text-bold">{{ props.row.code }}</span>
              </q-td>
            </template>

            <template v-slot:body-cell-actions="props">
              <q-td :props="props">
                <q-btn
                  unelevated
                  color="secondary"
                  flat
                  size="sm"
                  round
                  icon="las la-pen"
                  class="q-mr-sm"
                  @click="accounting_asset_mappings_dialog_open(props.row)"
                />

                <q-btn
                  v-if="props.row.rate_provider_asset_link"
                  dense
                  flat
                  round
                  icon="las la-external-link-alt"
                  target="_blank"
                  :href="props.row.rate_provider_asset_link"
                />
              </q-td>
            </template>
          </q-table>

          <!-- Display no search results -->
          <q-card
            v-if="accounting_asset_mappings.data.length == 0"
            class="q-mt-xl"
            flat
          >
            <q-card-section class="text-center">
              <q-icon
                name="las la-search"
                size="lg"
              />
              <h4 class="text-h6 q-mt-md">
                {{ i18n.accounting_asset_mappings_no_result }}
              </h4>
              <p class="text-caption">
                ({{ accounting_asset_mappings_search }})
              </p>
            </q-card-section>
            <q-inner-loading :showing="accounting_asset_mappings_table_loading">
              <q-spinner-tail
                size="50px"
                color="primary"
              />
            </q-inner-loading>
          </q-card>

          <div
            v-if="accounting_asset_mappings.meta"
            class="q-table__bottom row items-center justify-end"
          >
            <div class="q-table__control col-12">
              <Pagination
                :limits="[20, 50, 100, 200]"
                :meta="accounting_asset_mappings.meta"
                :prop_keys="['accounting_asset_mappings', 'pages']"
              />
            </div>
          </div>

          <!-- AccountingAssetMappings - Edit / Create -->
          <q-dialog v-model="accounting_asset_mappings_dialog_show">
            <q-card class="q-pa-md">
              <q-card-section>
                <div class="text-h6">
                  {{ i18n.accounting_asset_mappings_modify_code }}
                </div>
              </q-card-section>

              <q-card-section class="row q-col-gutter-md">
                <!-- AccountingAssetMapping Asset -->
                <div class="col-12">
                  <span class="text-input-label">{{ i18n.accounting_asset_mappings_asset }}</span>
                  <q-input
                    :model-value="accounting_asset_mappings_dialog_form.asset_name"
                    :prefix="accounting_asset_mappings_dialog_form.asset_symbol"
                    outlined
                    dense
                    readonly
                    class="full-width q-mt-sm"
                  />
                </div>

                <!-- AccountingAssetMapping Code -->
                <div class="col-12">
                  <span class="text-input-label">{{ i18n.accounting_asset_mappings_asset_code }}</span>
                  <q-input
                    v-model="accounting_asset_mappings_dialog_form.code"
                    outlined
                    autofocus
                    dense
                    class="full-width q-mt-sm"
                    :rules="[(val) => val?.trim() !== '' || i18n.required]"
                  />
                </div>
              </q-card-section>

              <q-card-actions
                align="right"
                class="q-mt-md"
              >
                <q-btn
                  :label="$q.lang.label.cancel"
                  color="secondary"
                  flat
                  @click="accounting_asset_mappings_dialog_show = false"
                />
                <q-btn
                  :label="i18n.submit"
                  color="primary"
                  unelevated
                  :disable="accounting_asset_mappings_dialog_save_disabled"
                  @click="accounting_asset_mappings_submit(accounting_asset_mappings_dialog_form)"
                />
              </q-card-actions>
            </q-card>
          </q-dialog>
        </q-tab-panel>

        <!-- ExchangeAccounts / AccountingSourceNumbers -->
        <q-tab-panel
          v-if="page_current == 'exchange_accounts' && exchange_accounts_workspaces.data"
          name="exchange_accounts"
          class="q-pa-none"
        >
          <div class="row q-mt-none items-center justify-between q-mb-lg">
            <div class="col-shrink text-left">
              <span class="text-h6">{{ i18n.exchange_accounts_title }}</span>
            </div>
            <div class="col-shrink row items-center">
              <!-- ExchangeAccounts - Search -->
              <q-input
                v-model="exchange_accounts_search"
                class="q-mr-md"
                outlined
                color="drawer-background"
                standout
                hide-bottom-space
                dense
                style="max-width: 240px"
                :placeholder="i18n.exchange_accounts_search"
              >
                <template v-slot:prepend>
                  <q-icon name="search" />
                </template>
                <template v-slot:append>
                  <q-icon
                    v-if="exchange_accounts_search != ''"
                    name="close"
                    class="cursor-pointer"
                    @click="exchange_accounts_search = ''"
                  />
                </template>
              </q-input>
            </div>
          </div>

          <q-card
            v-for="workspace in exchange_accounts_workspace_data_filtered"
            :key="workspace.id"
            class="q-mb-xl"
          >
            <!-- ExchangeAccounts - Workspace Header -->
            <q-card-section class="row items-center justify-space-between q-py-xs">
              <q-item class="q-px-none">
                <q-item-section
                  avatar
                  style="min-width: 36px"
                >
                  <q-avatar size="24px">
                    <Jdenticon
                      :text="workspace.id"
                      :size="24"
                    />
                  </q-avatar>
                </q-item-section>

                <q-item-section>
                  <q-item-label>
                    <span class="q-card-title">{{ workspace.name }}</span>
                  </q-item-label>
                </q-item-section>
              </q-item>
            </q-card-section>

            <q-card-section class="q-pa-none">
              <q-table
                :rows="workspace.exchange_accounts"
                :columns="exchange_accounts_columns"
                row-key="id"
                dense
                flat
                separator="none"
                :pagination="{
                  rowsPerPage: -1,
                  sortBy: 'exchange',
                  descending: false,
                }"
                hide-bottom
              >
                <template v-slot:body-cell-exchange="props">
                  <q-td :props="props">
                    <q-avatar
                      size="18px"
                      square
                      class="q-mr-sm"
                    >
                      <img
                        :alt="props.row.exchange"
                        :src="props.row.logo_url"
                      >
                    </q-avatar>
                    {{ props.row.exchange }}
                  </q-td>
                </template>

                <template v-slot:body-cell-accounting_source_number="props">
                  <q-td :props="props">
                    <span style="font-weight: 600">{{ props.value }}</span>
                  </q-td>
                </template>

                <template v-slot:body-cell-actions="props">
                  <q-td :props="props">
                    <q-btn
                      unelevated
                      color="secondary"
                      flat
                      size="sm"
                      round
                      icon="las la-pen"
                      class="q-mr-sm"
                      @click="exchange_accounts_edit(props.row)"
                    />
                  </q-td>
                </template>
              </q-table>
            </q-card-section>
          </q-card>

          <div
            v-if="exchange_accounts_workspace_data_filtered.length == 0"
            class="col-xs-12 col-sm-12 col-md-12"
          >
            <q-banner
              class="bg-pastel-green q-pa-md"
              dense
            >
              <template v-slot:avatar>
                <q-icon
                  name="las la-info-circle"
                  class="message-box-text"
                  size="sm"
                />
              </template>
              <div v-if="exchange_accounts_search == ''">
                {{ i18n.exchange_accounts_clients_empty }}
              </div>
              <div v-else>
                {{ i18n.exchange_accounts_search_empty }}
              </div>
            </q-banner>
          </div>

          <!-- ExchangeAccount - Edit / Create -->
          <q-dialog v-model="exchange_accounts_dialog_show">
            <q-card class="q-pa-md">
              <q-card-section>
                <div class="text-h6">
                  {{ i18n.exchange_accounts_source_number_edit }}
                </div>
              </q-card-section>

              <q-card-section class="row q-col-gutter-md">
                <!-- Compte -->
                <div class="col-12">
                  <span class="text-input-label">{{ i18n.exchange_accounts_account_number }}</span>
                  <q-input
                    :model-value="exchange_accounts_dialog_form.name"
                    outlined
                    dense
                    readonly
                    class="full-width q-mt-sm"
                  />
                </div>

                <!-- ExchangeAccount Code -->
                <div class="col-12">
                  <span class="text-input-label">{{ i18n.exchange_accounts_source_number }}</span>
                  <q-input
                    v-model="exchange_accounts_dialog_form.accounting_source_number"
                    outlined
                    autofocus
                    dense
                    class="full-width q-mt-sm"
                  />
                </div>

                <!-- ExchangeAccount journal_interpolation_code -->
                <div class="col-12">
                  <span class="text-input-label">{{ i18n.exchange_accounts_journal_code }}</span>
                  <q-input
                    v-model="exchange_accounts_dialog_form.journal_interpolation_code"
                    outlined
                    dense
                    class="full-width q-mt-sm"
                  />
                </div>

                <!-- ExchangeAccount journal_interpolation_lib -->
                <div class="col-12">
                  <span class="text-input-label">{{ i18n.exchange_accounts_journal_lib }}</span>
                  <q-input
                    v-model="exchange_accounts_dialog_form.journal_interpolation_lib"
                    outlined
                    dense
                    class="full-width q-mt-sm"
                  />
                </div>
              </q-card-section>

              <q-card-actions
                align="right"
                class="q-mt-md"
              >
                <q-btn
                  :label="$q.lang.label.cancel"
                  color="secondary"
                  flat
                  @click="exchange_accounts_dialog_show = false"
                />
                <q-btn
                  :label="i18n.submit"
                  color="primary"
                  unelevated
                  @click="exchange_accounts_update(exchange_accounts_dialog_form)"
                />
              </q-card-actions>
            </q-card>
          </q-dialog>
        </q-tab-panel>
      </q-tab-panels>

      <!-- Handle no accounting plans case ( should not happen )-->
      <div
        v-if="accounting_plans.length === 0"
        class="q-my-xl"
      >
        <q-banner
          class="bg-pastel-green q-px-md"
          dense
          style="width: fit-content; margin: auto"
        >
          <template v-slot:avatar>
            <q-icon
              name="las la-exclamation-circle"
              class="message-box-text"
              size="md"
            />
          </template>
          <div class="">
            {{ i18n.accounting_plans_empty }}
          </div>
        </q-banner>
      </div>
    </div>
  </div>

  <!-- AccountingPlan Dialog - Edit / Duplicate / Create -->
  <q-dialog v-model="accounting_plans_dialog_show">
    <q-card class="q-pa-md">
      <q-card-section>
        <div class="text-h6">
          {{ accounting_plans_dialog_title }}
        </div>
      </q-card-section>

      <q-card-section class="row q-col-gutter-md">
        <!-- Name -->
        <div class="col-12">
          <span class="text-input-label">{{ i18n.accounting_plans_form_name }}</span>
          <q-input
            v-model="accounting_plans_dialog_form.name"
            outlined
            dense
            class="full-width q-mt-sm"
          />
        </div>

        <!-- Description -->
        <div class="col-12">
          <span class="text-input-label">{{ i18n.accounting_plans_form_description }}</span>
          <q-input
            v-model="accounting_plans_dialog_form.description"
            outlined
            dense
            class="full-width q-mt-sm"
          />
        </div>

        <!-- Crypto Num Interpolation -->
        <div class="col-12">
          <span class="text-input-label">{{ i18n.accounting_plans_form_crypto_numeric_interpolation }}</span>
          <q-input
            v-model="accounting_plans_dialog_form.crypto_num_interpolation"
            outlined
            dense
            class="full-width q-mt-sm"
          />
        </div>

        <!-- Crypto Ecrlib Interpolation -->
        <div class="col-12">
          <span class="text-input-label">{{ i18n.accounting_plans_form_crypto_entry_label_interpolation }}</span>
          <q-input
            v-model="accounting_plans_dialog_form.crypto_ecrlib_interpolation"
            outlined
            dense
            class="full-width q-mt-sm"
          />
        </div>

        <!-- Fiat Num Interpolation -->
        <div class="col-12">
          <span class="text-input-label">{{ i18n.accounting_plans_form_fiat_numeric_interpolation }}</span>
          <q-input
            v-model="accounting_plans_dialog_form.fiat_num_interpolation"
            outlined
            dense
            class="full-width q-mt-sm"
          />
        </div>

        <!-- Fiat Ecrlib Interpolation -->
        <div class="col-12">
          <span class="text-input-label">{{ i18n.accounting_plans_form_fiat_entry_label_interpolation }}</span>
          <q-input
            v-model="accounting_plans_dialog_form.fiat_ecrlib_interpolation"
            outlined
            dense
            class="full-width q-mt-sm"
          />
        </div>
      </q-card-section>

      <q-card-actions
        align="right"
        class="q-mt-md"
      >
        <q-btn
          :label="$q.lang.label.cancel"
          color="primary"
          flat
          @click="accounting_plans_dialog_show = false"
        />
        <q-btn
          :label="accounting_plans_dialog_form.id ? i18n.edit : $q.lang.label.create"
          color="positive"
          unelevated
          :disable="accounting_plans_dialog_save_disabled"
          @click="accounting_plans_dialog_submit()"
        />
      </q-card-actions>

      <q-inner-loading :showing="accounting_plans_dialog_loading">
        <q-spinner-tail
          size="100px"
          color="primary"
        />
      </q-inner-loading>
    </q-card>
  </q-dialog>
</template>

<script>
import Layout from '@/layouts/wg_admin.vue';
import Jdenticon from '@/shared/jdenticon.vue';
import Pagination from '@/shared/pagination.vue';
import { toRaw } from 'vue';

export default {
  components: {
    Jdenticon,
    Pagination,
  },
  layout: Layout,
  props: {
    i18n: {
      type: Object,
      require: true,
    },
    pages: {
      type: Array,
      require: true,
    },
    page: {
      type: String,
      require: true,
    },
    accounting_plans: {
      type: Array,
      require: true,
    },
    accounting_plan: {
      type: Object,
      require: false,
    },
    transaction_kind_categories: {
      type: Array,
      require: false,
    },
    accounting_mappings_categories: {
      type: Array,
      require: false,
    },
    accounting_mappings_category_transaction_kinds: {
      type: Array,
      require: false,
    },
    accounting_mappings_category_uncategorized: {
      type: Array,
      require: false,
    },
    accounting_mappings_category_swap: {
      type: Array,
      require: false,
    },
    accounting_mappings_category_capital_gain_loss: {
      type: Array,
      require: false,
    },
    accounting_accounts: {
      type: Array,
      default: () => { return [] },
      require: false,
    },
    accounting_journals: {
      type: Array,
      default: () => { return [] },
      require: false,
    },
    accounting_asset_mappings: {
      type: Object,
      default: () => { return {} },
      require: false,
    },
    exchange_accounts_workspaces: {
      type: Object,
      default: () => { return {} },
      require: false,
    },
  },
  data() {
    return {
      page_current: this.page,
      // accounting_plans
      accounting_plans_selected: this.accounting_plan,
      accounting_plans_dialog_show: false,
      accounting_plans_dialog_loading: false,
      accounting_plans_dialog_form: {},
      // transaction_kinds
      transaction_kinds_dialog_show: false,
      transaction_kinds_dialog_loading: false,
      transaction_kinds_dialog_form: {},
      // accounting_mappings
      accounting_mappings_dialog_accounting_accounts: this.accounting_mappings_dialog_accounting_accounts_filter('', () => { }, () => { }),
      accounting_mappings_dialog_accounting_journals: this.accounting_mappings_dialog_accounting_journals_filter('', () => { }, () => { }),
      accounting_mappings_dialog_form: {},
      accounting_mappings_dialog_in_or_out_show: false,
      accounting_mappings_dialog_show: false,
      accounting_mappings_transaction_kinds_search: '',
      accounting_mappings_category_selected: 'accounting_mappings_category_transaction_kinds',
      accounting_mappings_view_selected: 'grid',
      accountingMappings: { positive: [], negative: [] },
      // accounting_accounts
      accounting_accounts_search: '',
      accounting_accounts_dialog_show: false,
      accounting_accounts_dialog_form: {},
      // accounting_journals
      accounting_journals_search: '',
      accounting_journals_dialog_show: false,
      accounting_journals_dialog_form: {},
      // accounting_asset_mappings
      accounting_asset_mappings_search: this.accounting_asset_mappings.search,
      accounting_asset_mappings_dialog_show: false,
      accounting_asset_mappings_dialog_form: {},
      accounting_asset_mappings_table_loading: false,
      // exchange_accounts
      exchange_accounts_search: this.exchange_accounts_workspaces.search,
      exchange_accounts_dialog_show: false,
      exchange_accounts_dialog_form: {},
      exchange_accounts_list_loading: false,
    };
  },
  remember: [
    'accounting_mappings_view_selected',
    'accounting_mappings_category_selected',
  ],
  computed: {
    accounting_plans_dialog_save_disabled: function () {
      return (this.accounting_plans_dialog_form.name ?? '').trim() == '' ||
        (this.accounting_plans_dialog_form.crypto_num_interpolation ?? '').trim() == '' ||
        (this.accounting_plans_dialog_form.crypto_ecrlib_interpolation ?? '').trim() == '' ||
        (this.accounting_plans_dialog_form.fiat_num_interpolation ?? '').trim() == '' ||
        (this.accounting_plans_dialog_form.fiat_ecrlib_interpolation ?? '').trim() == ''
      ;
    },
    transaction_kinds_dialog_save_disabled: function () {
      return (this.transaction_kinds_dialog_form.label_fr ?? '').trim() == '' ||
        (this.transaction_kinds_dialog_form.description ?? '').trim() == '' ||
        (this.transaction_kinds_dialog_form.transaction_kind_category_id ?? '').trim() == ''
      ;
    },
    accounting_mappings_transaction_kinds_filtered: function () {
      const search = this.accounting_mappings_transaction_kinds_search.toLowerCase();
      switch (this.accounting_mappings_category_selected) {
      case "accounting_mappings_category_transaction_kinds":
        if (search.length == 0) {
          return this.accounting_mappings_category_transaction_kinds;
        }
        return this.accounting_mappings_category_transaction_kinds.filter((tk) => {
          const label = tk.label_fr.toLowerCase().includes(search);
          const name = tk.name.toLowerCase().includes(search);
          return name || label;
        });
      case "accounting_mappings_category_uncategorized":
        return this.accounting_mappings_category_uncategorized;
      case "accounting_mappings_category_swap":
        return this.accounting_mappings_category_swap;
      case "accounting_mappings_category_capital_gain_loss":
        return this.accounting_mappings_category_capital_gain_loss;
      }
      return this.accounting_mappings_category_transaction_kinds;
    },
    accounting_mappings_list_columns: function () {
      return [
        {
          label: "",
          align: "left",
          sortable: false
        },
        {
          label: this.i18n.accounting_mapping_debit_account,
          align: "left",
          sortable: false,
        },
        {
          label: this.i18n.accounting_mapping_credit_account,
          align: "left",
          sortable: false,
        },
        {
          label: this.i18n.accounting_mapping_journal,
          align: "left",
          sortable: false
        },
        {
          label: this.i18n.accounting_mapping_description,
          align: "left",
          sortable: false
        },
        {
          label: this.i18n.accounting_mapping_total_amount_percent,
          align: "left",
          sortable: false,
        },
        {
          label: this.i18n.accounting_mapping_vat,
          align: "left",
          sortable: false
        },
        {
          label: this.i18n.accounting_mapping_base,
          align: "left",
          sortable: false
        },
        {
          label: "",
          align: "right",
          sortable: false
        },
      ];
    },
    accounting_mappings_dialog_save_disabled: function () {
      return (this.accounting_mappings_dialog_form.ecriture_lib ?? '').trim() == '' ||
        (this.accounting_mappings_dialog_form.accounting_journal_id ?? '').trim() == '' ||
        (this.accounting_mappings_dialog_form.debit_accounting_account_id ?? '').trim() == '' ||
        (this.accounting_mappings_dialog_form.credit_accounting_account_id ?? '').trim() == ''
    },
    accounting_accounts_filtered: function () {
      const search = this.accounting_accounts_search.toLowerCase();
      if (search.length == 0) {
        return this.accounting_accounts;
      }
      return this.accounting_accounts.filter((aa) => {
        const num = aa.account_num.toLowerCase().includes(search);
        const lib = aa.account_lib.toLowerCase().includes(search);
        return num || lib;
      });
    },
    accounting_accounts_columns: function () {
      return [
        {
          name: "account_num",
          label: this.i18n.account_number,
          field: "account_num",
          align: "left",
          sortable: true
        },
        {
          name: "account_lib",
          label: this.i18n.account_lib,
          field: "account_lib",
          align: "left",
          sortable: true
        },
        {
          name: "actions",
          label: "",
          field: "actions",
          align: "right",
          sortable: false,
        },
      ];
    },
    accounting_accounts_dialog_save_disabled: function () {
      return (this.accounting_accounts_dialog_form.account_num ?? '').trim() == '' ||
        (this.accounting_accounts_dialog_form.account_lib ?? '').trim() == '';
    },
    accounting_journals_filtered: function () {
      const search = this.accounting_journals_search.toLowerCase();
      if (search.length == 0) {
        return this.accounting_journals;
      }
      return this.accounting_journals.filter((aj) => {
        const code = aj.journal_code.toLowerCase().includes(search);
        const lib = aj.journal_lib.toLowerCase().includes(search);
        return code || lib;
      });
    },
    accounting_journals_columns: function () {
      return [
        {
          name: "journal_code",
          label: this.i18n.exchange_accounts_journal_code,
          field: "journal_code",
          align: "left",
          sortable: true
        },
        {
          name: "journal_lib",
          label: this.i18n.exchange_accounts_journal_lib,
          field: "account_lib",
          align: "left",
          sortable: true
        },
        {
          name: "actions",
          label: "",
          field: "actions",
          align: "right",
          sortable: false,
        },
      ];
    },
    accounting_journals_dialog_save_disabled: function () {
      return (this.accounting_journals_dialog_form.journal_code ?? '').trim() == '' ||
        (this.accounting_journals_dialog_form.journal_lib ?? '').trim() == '';
    },
    accounting_asset_mappings_dialog_save_disabled: function () {
      return (this.accounting_asset_mappings_dialog_form.asset_name ?? '').trim() == '' ||
        (this.accounting_asset_mappings_dialog_form.code ?? '').trim() == '';
      ;
    },
    accounting_asset_mappings_columns: function () {
      return [
        {
          name: "asset_symbol",
          label: this.i18n.accounting_asset_mappings_asset,
          field: (row) => row.asset?.symbol,
          align: "left",
          sortable: true
        },
        {
          name: "code",
          label: this.i18n.accounting_asset_mappings_asset_code,
          field: "code",
          align: "left",
          sortable: true
        },
        {
          name: "actions",
          label: "",
          field: "actions",
          align: "right",
          sortable: false,
        }
      ];
    },
    accounting_asset_mappings_table_pagination: function () {
      return {
        rowsPerPage: this.accounting_asset_mappings?.data?.length,
        sortBy: 'asset_symbol',
        descending: true,
      }
    },
    exchange_accounts_columns: function () {
      return [
        {
          name: "exchange",
          label: this.i18n.exchange_accounts_platform,
          field: (row) => row.exchange,
          align: "left",
          sortable: true,
          style: "width: 18%",
        },
        {
          name: "name",
          label: this.i18n.exchange_accounts_account_number,
          field: (row) => row.name,
          align: "left",
          sortable: true,
          style: "width: 18%"
        },
        {
          name: "accounting_source_number",
          label: this.i18n.exchange_accounts_source_number,
          field: (row) => row.accounting_source_number,
          align: "left",
          sortable: true,
          style: "width: 18%",
        },
        {
          name: "journal_interpolation_code",
          label: this.i18n.exchange_accounts_journal_code,
          field: (row) => row.journal_interpolation_code,
          align: "left",
          sortable: true,
          style: "width: 18%",
        },
        {
          name: "journal_interpolation_lib",
          label: this.i18n.exchange_accounts_journal_lib,
          field: (row) => row.journal_interpolation_lib,
          align: "left",
          sortable: true,
          style: "width: 18%",
        },
        {
          name: "actions",
          label: "",
          field: "actions",
          align: "right",
          sortable: false,
          style: "width: 10%",
        },
      ];
    },
    exchange_accounts_workspace_data_filtered() {
      const search = this.exchange_accounts_search?.toLowerCase();
      if (!search) {
        return this.exchange_accounts_workspaces.data;
      }

      return this.exchange_accounts_workspaces.data.filter((d) => {
        return d.name.toLowerCase().includes(search);
      });
    },
  },
  watch: {
    'accounting_mappings_dialog_form.is_amount_splitted'(new_value) {
      if (new_value && this.accounting_mappings_dialog_form.total_amount_percent == 100) {
        this.accounting_mappings_dialog_form.total_amount_percent = 50;
      }
      if (!new_value) {
        this.accounting_mappings_dialog_form.total_amount_percent = 100;
      }
    },
    'accounting_mappings_dialog_form.show_tva_settings'(new_value) {
      if (new_value && this.accounting_mappings_dialog_form.tax_percent == 0) {
        this.accounting_mappings_dialog_form.tax_percent = 20;
      }
      if (!new_value) {
        this.accounting_mappings_dialog_form.tax_percent = 0;
      }
    },
    accounting_plans_selected(new_value) {
      this.$inertia.get(
        new_value,
        {}, // query params
        {
          preserveState: true,
          preserveScroll: true,
        },
      );
    },
    accounting_asset_mappings_search(new_value) {
      if (this.page_current != 'accounting_asset_mappings') {
        return;
      }

      this.accounting_asset_mappings_table_loading = true;
      this.$inertia.get(
        this.$page.props.accounting_asset_mappings.search_path,
        { search: new_value },
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: ['accounting_asset_mappings', 'pages'],
          onStart: () => {
            this.accounting_asset_mappings_table_loading = true;
          },
          onFinish: () => {
            this.accounting_asset_mappings_table_loading = false;
          },
        },
      );
    },
  },
  mounted() {
    // Auto-scroll to transaction kind if transaction_kind_id is in URL params
    this.$nextTick(() => {
      const urlParams = new URLSearchParams(window.location.search);
      const transactionKindId = urlParams.get('transaction_kind_id');

      if (transactionKindId) {
        // Use the ID directly to find the element
        const elementId = `transaction-kind-${transactionKindId}`;
        const element = document.getElementById(elementId);

        if (element) {
          // Scroll to the element with offset for header
          setTimeout(() => {
            // Get the header height to account for sticky positioning
            const header = document.querySelector('.q-header');
            const headerHeight = header ? header.offsetHeight : 0;

            // Calculate the position to scroll to, with additional padding
            const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
            const offsetPosition = elementPosition - headerHeight - 20; // 20px additional padding

            // Scroll to the calculated position
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });

            // Add a highlight effect
            element.classList.add('highlight-transaction-kind');
            setTimeout(() => {
              element.classList.remove('highlight-transaction-kind');
            }, 3000);
          }, 500);
        }
      }
    });
  },
  methods: {
    percent_color: function (value) {
      if (value >= 100) return "positive";
      if (value >= 75) return "accent";
      if (value >= 50) return "info";
      if (value >= 25) return "warning";
      return "negative";
    },
    page_select: function (link) {
      this.$inertia.get(
        link,
        {}, // query params
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
        },
      )
    },
    // accounting_plan
    accounting_plans_edit: function (ap) {
      if (ap.id == void 0) {
        ap = {
          name: "",
          description: "",
          crypto_num_interpolation: "22",
          crypto_ecrlib_interpolation: "Jetons detenus",
          fiat_num_interpolation: "17",
          fiat_ecrlib_interpolation: "Autres organismes financiers",
        };
      }
      if (ap.id) {
        this.accounting_plans_dialog_title = this.i18n.accounting_plans_edit
      } else {
        this.accounting_plans_dialog_title = this.i18n.accounting_plans_add
      }
      this.accounting_plans_dialog_show = true;
      this.accounting_plans_dialog_form = {
        id: ap.id,
        name: ap.name,
        description: ap.description,
        crypto_num_interpolation: ap.crypto_num_interpolation,
        crypto_ecrlib_interpolation: ap.crypto_ecrlib_interpolation,
        fiat_num_interpolation: ap.fiat_num_interpolation,
        fiat_ecrlib_interpolation: ap.fiat_ecrlib_interpolation,
      };
      this.accounting_plans_dialog_form = structuredClone(toRaw(ap));
    },
    accounting_plans_delete_confirm: function (ap) {
      this.$q.dialog({
        title: this.i18n.accounting_plans_delete_confirm_title,
        message: this.i18n.accounting_plans_delete_confirm_message,
        cancel: true,
        color: "primary",
        persistent: true,
      }).onOk(() => {
        this.accounting_plans_delete(ap);
      });
    },
    accounting_plans_duplicate_confirm: function (ap) {
      this.$q.dialog({
        title: this.i18n.accounting_plans_duplicate_confirm_title,
        message: this.i18n.accounting_plans_duplicate_confirm_message,
        cancel: true,
        color: "primary",
        persistent: true,
      }).onOk(async () => {
        this.accounting_plans_duplicate(ap);
      });
    },
    accounting_plans_dialog_submit: function () {
      if (this.accounting_plans_dialog_form.id) {
        this.accounting_plans_update(this.accounting_plans_dialog_form);
      } else {
        this.accounting_plans_create(this.accounting_plans_dialog_form);
      }
    },
    accounting_plans_create: function (ap) {
      this.$inertia.post(
        '../',
        ap,
        {
          preserveState: true,
          preserveScroll: true,
          onStart: () => {
            this.accounting_plans_dialog_loading = true;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.i18n.accounting_plans_created,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: errors,
              type: "negative",
            });
          },
          onFinish: () => {
            this.accounting_plans_dialog_loading = false;
            this.accounting_plans_dialog_show = false;
          },
        }
      );
    },
    accounting_plans_duplicate: function (ap) {
      this.$inertia.post(
        '../duplicate',
        { duplicate_id: ap.id },
        {
          preserveState: true,
          preserveScroll: true,
          onStart: () => {
            this.accounting_plans_dialog_loading = true;
            this.accounting_plans_dialog_show = true;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.i18n.accounting_plans_duplicated,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: errors,
              type: "negative",
            });
          },
          onFinish: () => {
            this.accounting_plans_dialog_loading = false;
            this.accounting_plans_dialog_show = false;
          },
        }
      );
    },
    accounting_plans_update: function (ap) {
      this.$inertia.put(
        `../${ap.id}`,
        ap,
        {
          preserveState: true,
          preserveScroll: true,
          onStart: () => {
            this.accounting_plans_dialog_loading = true;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.i18n.accounting_plans_updated,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: errors,
              type: "negative",
            });
          },
          onFinish: () => {
            this.accounting_plans_dialog_loading = false;
            this.accounting_plans_dialog_show = false;
          },
        }
      );
    },
    accounting_plans_delete: function (ap) {
      this.$inertia.delete(
        `../${ap.id}`,
        {
          preserveState: true,
          preserveScroll: true,
          onStart: () => {
            this.accounting_plans_dialog_show = false;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.i18n.accounting_plans_deleted,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: errors,
              type: "negative",
            });
          },
        }
      );
    },

    // transaction_kinds
    transaction_kinds_edit: function (tk) {
      if (tk.id == void 0) {
        tk = {
          label_fr: "",
          system_default: false,
          description: "",
          transaction_kind_category_id: "",
          ignore_transaction: false,
          calculate_capital_gain_loss: true,
        };
      }
      if (tk.id) {
        this.transaction_kinds_dialog_title = this.i18n.operation_type_edit_title;
        if (tk.system_default) {
          this.transaction_kinds_dialog_title = this.i18n.operation_type_edit_system_title;
        }
      } else {
        this.transaction_kinds_dialog_title = this.i18n.operation_type_add_title;
      }
      this.transaction_kinds_dialog_show = true;
      this.transaction_kinds_dialog_form = structuredClone(toRaw(tk));
    },
    transaction_kinds_delete_confirm: function (tk) {
      this.$q.dialog({
        title: this.i18n.operation_type_delete_confirm_title,
        message: `${tk.label_fr} \n\n ${this.i18n.operation_type_delete_confirm_message}`,
        cancel: true,
        color: "primary",
        cardClass: "whitespace-pre-line",
        persistent: true,
      }).onOk(() => {
        this.transaction_kinds_delete(tk);
      });
    },
    transaction_kinds_dialog_submit: function () {
      if (this.transaction_kinds_dialog_form.id) {
        this.transaction_kinds_update(this.transaction_kinds_dialog_form);
      } else {
        this.transaction_kinds_create(this.transaction_kinds_dialog_form);
      }
    },
    transaction_kinds_create: function (tk) {
      this.$inertia.post(
        'transaction_kinds',
        tk,
        {
          preserveState: true,
          preserveScroll: false,
          onStart: () => {
            this.transaction_kinds_dialog_show = false;
          },
          onSuccess: (data) => {
            console.log('data:', data);
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });

            // Scroll to new TK
            this.$nextTick(() => {
              const el = this.$refs[tk.label_fr][0].getBoundingClientRect();
              const offset = 100;
              window.scrollTo({
                top: window.scrollY + el.top - offset,
                behavior: "smooth",
              });
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
        }
      );
    },
    transaction_kinds_update: function (tk) {
      this.$inertia.put(
        `transaction_kinds/${tk.id}`,
        tk,
        {
          preserveState: true,
          preserveScroll: true,
          onStart: () => {
            this.transaction_kinds_dialog_show = false;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
        }
      );
    },
    transaction_kinds_delete: function (tk) {
      this.$inertia.delete(
        `transaction_kinds/${tk.id}`,
        {
          preserveState: true,
          preserveScroll: true,
          onStart: () => {
            this.transaction_kinds_dialog_show = false;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
        }
      );
    },

    // accounting_mappings
    accounting_mappings_dialog_accounting_accounts_filter: function (val, update, abort) {
      let options = this.accounting_accounts.filter((a) => {
        return val.length == 0 || a.account_num.toLowerCase().includes(val) || a.account_lib.toLowerCase().includes(val);
      }).map((a) => {
        return {
          id: a.id,
          label: `${a.account_num} - ${a.account_lib}`,
        }
      });
      update(() => {
        this.accounting_mappings_dialog_accounting_accounts = options;
      });
      abort(() => { });
      return options;
    },
    accounting_mappings_dialog_accounting_journals_filter: function (val, update, abort) {
      let options = this.accounting_journals.filter((a) => {
        return val.length == 0 || a.journal_code.toLowerCase().includes(val) || a.journal_lib.toLowerCase().includes(val);
      }).map((a) => {
        return {
          id: a.id,
          label: `${a.journal_code} - ${a.journal_lib}`,
        }
      });
      update(() => {
        this.accounting_mappings_dialog_accounting_journals = options;
      });
      abort(() => { });
      return options;
    },
    accounting_mappings_dialog_in_or_out_open: function (transaction_kind, order) {
      this.accounting_mappings_dialog_form = {};
      this.accounting_mappings_dialog_form.transaction_kind = transaction_kind;
      this.accounting_mappings_dialog_form.am_order = order;
      this.accounting_mappings_dialog_in_or_out_show = true;
    },
    accounting_mappings_dialog_form_inverse_accounts: function () {
      let temp = this.accounting_mappings_dialog_form.credit_accounting_account_id;
      this.accounting_mappings_dialog_form.credit_accounting_account_id = this.accounting_mappings_dialog_form.debit_accounting_account_id;
      this.accounting_mappings_dialog_form.debit_accounting_account_id = temp;
    },
    accounting_mappings_add: function (type) {
      let am = {};
      am.positive_amount = type == 'in';
      am.accounting_journal_id = null;
      am.amount_at_wac = false;
      am.credit_accounting_account_id = null;
      am.debit_accounting_account_id = null;
      am.ecriture_lib = "";
      am.is_amount_splitted = false;
      am.is_tax = false;
      am.show_tva_settings = false;
      am.tax_percent = 0;
      am.total_amount_percent = 100;
      this.accounting_mappings_edit(
        am,
        this.accounting_mappings_dialog_form.am_order,
        this.accounting_mappings_dialog_form.transaction_kind,
      );
      this.accounting_mappings_dialog_in_or_out_show = false;
    },
    accounting_mappings_duplicate: function (am, order, transaction_kind) {
      am = structuredClone(toRaw(am));
      am.id = null;
      this.accounting_mappings_edit(am, order, transaction_kind);
    },
    accounting_mappings_edit: function (am, order, transaction_kind) {
      am = structuredClone(toRaw(am));
      if (am.id) {
        this.accounting_mappings_dialog_title_in = this.i18n.accounting_mapping_incoming_transaction_modify;
        this.accounting_mappings_dialog_title_out = this.i18n.accounting_mapping_outgoing_transaction_modify;
      } else {
        this.accounting_mappings_dialog_title_in = this.i18n.accounting_mapping_incoming_transaction_add;
        this.accounting_mappings_dialog_title_out = this.i18n.accounting_mapping_outgoing_transaction_add;
      }
      if (transaction_kind.id == "suspense_account") {
        am.for_trade = false;
        am.for_capital_gain_loss = false;
      }
      if (transaction_kind.id == "swap") {
        this.accounting_mappings_dialog_title_in = this.i18n.accounting_mapping_swap_transaction_modify;
        am.for_trade = true;
        am.for_capital_gain_loss = false;
      }
      if (transaction_kind.id == "capital_gain_loss") {
        am.for_trade = false;
        am.for_capital_gain_loss = true;
      }
      am.order = order;
      am.is_amount_splitted = am.total_amount_percent != 100;
      am.show_tva_settings = am.tax_percent != 0;
      am.transaction_kind = transaction_kind;
      am.transaction_kind_id = transaction_kind.id;
      am.transaction_kind_category_label = this.transaction_kind_categories.find(
        tk => tk.id == transaction_kind.transaction_kind_category_id
      )?.label_fr
      this.accounting_mappings_dialog_form = am;
      this.accounting_mappings_dialog_show = true;
    },
    accounting_mappings_delete_confirm: function (am) {
      this.$q.dialog({
        title: this.i18n.accounting_mapping_delete_confirm_title,
        message: `${am.ecriture_lib} \n\n ${this.i18n.accounting_mapping_delete_confirm_message}`,
        cancel: true,
        color: "primary",
        cardClass: "whitespace-pre-line",
        persistent: true,
      }).onOk(() => {
        this.accounting_mappings_delete(am);
      });
    },
    accounting_mappings_dialog_submit: function () {
      if (!this.accounting_mappings_dialog_form.show_tva_settings) {
        this.accounting_mappings_dialog_form.tax_percent = 0;
        this.accounting_mappings_dialog_form.is_tax = false;
      }
      if (this.accounting_mappings_dialog_form.id) {
        this.accounting_mappings_update(this.accounting_mappings_dialog_form);
      } else {
        this.accounting_mappings_create(this.accounting_mappings_dialog_form);
      }
    },
    accounting_mappings_create: function (am) {
      this.$inertia.post(
        '',
        am,
        {
          preserveState: true,
          preserveScroll: true,
          onStart: () => {
            this.accounting_mappings_dialog_show = false;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
        }
      );
    },
    accounting_mappings_update: function (am) {
      this.$inertia.put(
        `accounting_mappings/${am.id}`,
        am,
        {
          preserveState: true,
          preserveScroll: true,
          onStart: () => {
            this.accounting_mappings_dialog_show = false;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
        }
      );
    },
    accounting_mappings_delete: function (am) {
      this.$inertia.delete(
        `accounting_mappings/${am.id}`,
        {
          preserveState: true,
          preserveScroll: true,
          onStart: () => {
            this.accounting_mappings_dialog_show = false;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
        }
      );
    },

    // accounting_accounts
    accounting_accounts_edit: function (aa) {
      if (aa.id) {
        this.accounting_accounts_dialog_title = this.i18n.account_edit
      } else {
        this.accounting_accounts_dialog_title = this.i18n.account_add
      }
      this.accounting_accounts_dialog_show = true;
      this.accounting_accounts_dialog_form = structuredClone(toRaw(aa));
    },
    accounting_accounts_delete_confirm: function (aa) {
      this.$q.dialog({
        title: this.i18n.account_delete_title,
        message: `${aa.account_num} - ${aa.account_lib} \n\n ${this.i18n.account_delete_message}`,
        cancel: true,
        color: "primary",
        cardClass: "whitespace-pre-line",
        persistent: true,
      }).onOk(() => {
        this.accounting_accounts_delete(aa);
      });
    },
    accounting_accounts_dialog_submit: function () {
      if (this.accounting_accounts_dialog_form.id) {
        this.accounting_accounts_update(this.accounting_accounts_dialog_form);
      } else {
        this.accounting_accounts_create(this.accounting_accounts_dialog_form);
      }
    },
    accounting_accounts_create: function (aa) {
      this.$inertia.post(
        '',
        aa,
        {
          preserveState: true,
          preserveScroll: true,
          onStart: () => {
            this.accounting_accounts_dialog_show = false;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.i18n.account_created,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.i18n.account_error,
              type: "negative",
            });
          },
        }
      );
    },
    accounting_accounts_update: function (aa) {
      this.$inertia.put(
        `accounting_accounts/${aa.id}`,
        aa,
        {
          preserveState: true,
          preserveScroll: true,
          onStart: () => {
            this.accounting_accounts_dialog_show = false;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.i18n.account_updated,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.i18n.account_error,
              type: "negative",
            });
          },
        }
      );
    },
    accounting_accounts_delete: function (aa) {
      this.$inertia.delete(
        `accounting_accounts/${aa.id}`,
        {
          preserveState: true,
          preserveScroll: true,
          onStart: () => {
            this.accounting_accounts_dialog_show = false;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.i18n.account_deleted,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.i18n.account_error,
              type: "negative",
            });
          },
        }
      );
    },

    // accounting_journals
    accounting_journals_edit: function (aj) {
      if (aj.id) {
        this.accounting_journals_dialog_title = this.i18n.journal_edit
      } else {
        this.accounting_journals_dialog_title = this.i18n.journal_add
      }
      this.accounting_journals_dialog_show = true;
      this.accounting_journals_dialog_form = structuredClone(toRaw(aj));
    },
    accounting_journals_delete_confirm: function (aj) {
      this.$q.dialog({
        title: this.i18n.journal_delete_title,
        message: `${aj.journal_code} - ${aj.journal_lib} \n\n ${this.i18n.journal_delete_message}`,
        cancel: true,
        color: "primary",
        cardClass: "whitespace-pre-line",
        persistent: true,
      }).onOk(() => {
        this.accounting_journals_delete(aj);
      });
    },
    accounting_journals_dialog_submit: function () {
      if (this.accounting_journals_dialog_form.id) {
        this.accounting_journals_update(this.accounting_journals_dialog_form);
      } else {
        this.accounting_journals_create(this.accounting_journals_dialog_form);
      }
    },
    accounting_journals_create: function (aj) {
      this.$inertia.post(
        '',
        aj,
        {
          preserveState: true,
          preserveScroll: true,
          onStart: () => {
            this.accounting_journals_dialog_show = false;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.i18n.journal_created,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.i18n.journal_error,
              type: "negative",
            });
          },
        }
      );
    },
    accounting_journals_update: function (aj) {
      this.$inertia.put(
        `accounting_journals/${aj.id}`,
        aj,
        {
          preserveState: true,
          preserveScroll: true,
          onStart: () => {
            this.accounting_journals_dialog_show = false;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.i18n.journal_updated,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.i18n.journal_error,
              type: "negative",
            });
          },
        }
      );
    },
    accounting_journals_delete: function (aj) {
      this.$inertia.delete(
        `accounting_journals/${aj.id}`,
        {
          preserveState: true,
          preserveScroll: true,
          onStart: () => {
            this.accounting_journals_dialog_show = false;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.i18n.journal_deleted,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.i18n.journal_error,
              type: "negative",
            });
          },
        }
      );
    },

    // accounting_asset_mappings
    accounting_asset_mappings_dialog_open: function (aam) {
      this.accounting_asset_mappings_dialog_show = true;
      this.accounting_asset_mappings_dialog_form = structuredClone(toRaw(aam));
    },
    accounting_asset_mappings_submit: function (aam) {
      if (aam.id) {
        this.accounting_asset_mappings_update(aam);
      } else {
        this.accounting_asset_mappings_create(aam);
      }
    },
    accounting_asset_mappings_create: function (aam) {
      aam.search = this.accounting_asset_mappings.search;
      aam.per_page = this.accounting_asset_mappings.meta.limit;
      aam.page = this.accounting_asset_mappings.meta.page;
      this.$inertia.post(
        `accounting_asset_mappings`,
        aam,
        {
          preserveState: true,
          preserveScroll: true,
          onStart: () => {
            this.accounting_asset_mappings_dialog_show = false;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
        }
      );
    },
    accounting_asset_mappings_update: function (aam) {
      aam.search = this.accounting_asset_mappings.search;
      aam.per_page = this.accounting_asset_mappings.meta.limit;
      aam.page = this.accounting_asset_mappings.meta.page;
      this.$inertia.put(
        `accounting_asset_mappings/${aam.id}`,
        aam,
        {
          preserveState: true,
          preserveScroll: true,
          onStart: () => {
            this.accounting_asset_mappings_dialog_show = false;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
        }
      );
    },

    // exchange_accounts
    exchange_accounts_edit: function (ea) {
      this.exchange_accounts_dialog_show = true;
      this.exchange_accounts_dialog_form = structuredClone(toRaw(ea));
    },
    exchange_accounts_update: function (ea) {
      ea.search = this.exchange_accounts_workspaces.search;
      ea.per_page = this.exchange_accounts_workspaces.meta.limit;
      ea.page = this.exchange_accounts_workspaces.meta.page;
      this.$inertia.put(
        `exchange_accounts/${ea.id}`,
        ea,
        {
          preserveState: true,
          preserveScroll: true,
          onStart: () => {
            this.exchange_accounts_dialog_show = false;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
        }
      );
    },
  },
};
</script>

<style>
.highlight-transaction-kind {
  position: relative;
  animation: highlight-elegant 2.5s ease-out;
  border-radius: 12px;
  overflow: visible;
}

.highlight-transaction-kind::before {
  content: '';
  position: absolute;
  inset: -3px;
  background: linear-gradient(90deg,
      transparent,
      rgba(33, 150, 243, 0.6),
      rgba(0, 188, 212, 0.6),
      rgba(33, 150, 243, 0.6),
      transparent);
  background-size: 200% 100%;
  border-radius: 14px;
  opacity: 0;
  z-index: -1;
  animation: sweep-border 2.5s ease-out;
}

.highlight-transaction-kind::after {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at center,
      rgba(33, 150, 243, 0.15) 0%,
      rgba(33, 150, 243, 0.05) 40%,
      transparent 70%);
  border-radius: 12px;
  opacity: 0;
  animation: pulse-bg 2.5s ease-out;
}

@keyframes highlight-elegant {
  0% {
    transform: scale(1) translateY(0);
    filter: brightness(1);
  }

  15% {
    transform: scale(1.02) translateY(-2px);
    filter: brightness(1.1);
  }

  30% {
    transform: scale(1.01) translateY(-1px);
    filter: brightness(1.05);
  }

  100% {
    transform: scale(1) translateY(0);
    filter: brightness(1);
  }
}

@keyframes sweep-border {
  0% {
    background-position: -100% 0;
    opacity: 0;
  }

  20% {
    opacity: 1;
  }

  50% {
    background-position: 100% 0;
    opacity: 1;
  }

  100% {
    background-position: 200% 0;
    opacity: 0;
  }
}

@keyframes pulse-bg {
  0% {
    opacity: 0;
    transform: scale(0.8);
  }

  30% {
    opacity: 1;
    transform: scale(1);
  }

  70% {
    opacity: 0.5;
    transform: scale(1.05);
  }

  100% {
    opacity: 0;
    transform: scale(1.1);
  }
}

.flip-in-x {
  animation: flipInX 0.2s forwards;
}

.flip-out-x {
  animation: flipOutX 0.2s forwards;
}

@keyframes flipInX {
  from {
    transform: rotateX(90deg);
  }

  to {
    transform: rotateX(0deg);
  }
}

@keyframes flipOutX {
  from {
    transform: rotateX(0deg);
  }

  to {
    transform: rotateX(-90deg);
  }
}
</style>
